'use client';

import { useEffect, useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { Filter, Search, X, Zap } from 'lucide-react';

/** Listing-type filters — 1:1 with legacy `ProductTypePicker`. */
const TYPE_FILTERS = [
  { value: 'DIGITAL', label: 'Digital Downloads' },
  { value: 'EXTERNAL', label: 'External Products' },
] as const;

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  /** Current keyword (from the URL) — seeds the input for SSR + back-nav. */
  keyword: string;
  /** Current listing-type filters (from the URL). */
  includeTypes: string[];
  /**
   * State-driven host override. The visitor route omits it — search/filter
   * live in the URL (`?q=&type=`) and the server re-renders the grid. The
   * creator's `/my-popstore` collection aside has NO route reading those
   * params (the aside is component state), so it passes `onChange` and owns
   * the values in React state instead; the URL is left untouched there.
   */
  onChange?: (keyword: string, includeTypes: string[]) => void;
}

/**
 * Visitor collection-detail search + type filter — the ONLY client state on the
 * page, and it lives in the URL, not React. Typing (debounced) and toggling a
 * filter `router.replace` a new `?q=&type=` query; the server re-renders the
 * product grid from those params. `page` is dropped on every change so a new
 * search/filter always restarts at page 1. Shareable + crawlable: every filtered
 * view is a real URL. (State-driven hosts swap the URL write for `onChange` —
 * see the prop.)
 */
export function CollectionControls({ keyword, includeTypes, onChange }: Props) {
  const router = useRouter();
  const pathname = usePathname();

  const [input, setInput] = useState(keyword);

  const pushQuery = (nextKeyword: string, nextTypes: string[]) => {
    const trimmed = nextKeyword.trim();
    if (onChange) {
      onChange(trimmed, nextTypes);
      return;
    }
    const params = new URLSearchParams();
    if (trimmed) {
      params.set('q', trimmed);
    }
    if (nextTypes.length > 0) {
      params.set('type', nextTypes.join(','));
    }
    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false,
    });
  };

  // Debounce the keyword → URL. Only fires when the trimmed input diverges from
  // the URL keyword, so re-renders driven by the resulting nav don't re-push.
  useEffect(() => {
    if (input.trim() === keyword.trim()) {
      return;
    }
    const timeoutId = window.setTimeout(
      () => pushQuery(input, includeTypes),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [input, keyword]);

  const toggleType = (value: string) => {
    const next = includeTypes.includes(value)
      ? includeTypes.filter(type => type !== value)
      : [...includeTypes, value];
    pushQuery(input, next);
  };

  return (
    <div className="border-raise/10 flex w-full shrink-0 items-center gap-2 border-b px-3 py-3">
      <div className="relative flex flex-1 items-center">
        <Zap className="text-brand-text absolute left-3 h-4 w-4" aria-hidden />
        <input
          type="text"
          value={input}
          onChange={event => setInput(event.target.value)}
          placeholder="Search by keyword..."
          // 16px on mobile — iOS Safari auto-zooms the page when a focused
          // input's font-size is below 16px (QA: the screen zoomed in when
          // the collection search field got focus). `sm:` restores the
          // designed 13px on desktop-class viewports, which never zoom.
          className="placeholder:text-brand-text/60 border-raise/15 text-content-1 focus:border-raise/30 text-body h-10 w-full rounded-full border bg-transparent pl-9 pr-10 outline-none sm:text-[13px]"
        />
        {input ? (
          <button
            type="button"
            onClick={() => setInput('')}
            aria-label="Clear search"
            className="text-raise/60 hover:text-content-1 absolute right-3 flex h-5 w-5 items-center justify-center"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : (
          <Search
            className="text-brand-text pointer-events-none absolute right-3 h-4 w-4"
            aria-hidden
          />
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Filter products"
            className="border-brand text-brand-text hover:bg-brand/10 relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition"
          >
            <Filter className="h-4 w-4" aria-hidden />
            {includeTypes.length > 0 ? (
              <span className="bg-brand text-content-on-brand text-label absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 font-semibold">
                {includeTypes.length}
              </span>
            ) : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="font-display border-line bg-surface-1 text-content-1 min-w-[200px]"
        >
          {TYPE_FILTERS.map(option => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={includeTypes.includes(option.value)}
              onCheckedChange={() => toggleType(option.value)}
              className="text-content-1 focus:bg-line focus:text-content-1"
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
